import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Paper,
  CardContent,
  Snackbar,
  Alert,
} from '@mui/material';
import type { AlertColor } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import LevelIcon from '@mui/icons-material/SignalCellularAlt';
import PlayIcon from '@mui/icons-material/PlayCircle';
import LockIcon from '@mui/icons-material/Lock';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import RecommendedIcon from '@mui/icons-material/AutoStories';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import DescriptionIcon from '@mui/icons-material/Description';
import ListIcon from '@mui/icons-material/List';
import SurveyIcon from '@mui/icons-material/AssignmentTurnedIn';
import QuizIcon from '@mui/icons-material/Quiz';
import VideoIcon from '@mui/icons-material/PlayCircle';
import AudioIcon from '@mui/icons-material/Headphones';
import ArticleIcon from '@mui/icons-material/Article';
import ScormIcon from '@mui/icons-material/School';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DefaultLessonIcon from '@mui/icons-material/Help';
import { Link } from 'react-router-dom';
import { Card, Avatar } from '@mui/material';
import DOMPurify from 'dompurify';
import BookmarkButton from '../components/BookmarkButton';
import CourseCreditLimitHint from '../components/CourseCreditLimitHint';
import AIAssistant from '../components/AIAssistant';
import NasbaDisclosures from '../components/NasbaDisclosures';
import { NASBA_HANDLED_DESCRIPTION_TYPES, getDescriptionTypeLabel } from '../utils/nasbaDisclosure';
import type { Course } from '../services/courseService';
import { getFieldsOfStudy } from '../utils/fieldOfStudy';
import type { Registration, RegistrationStatus } from '../services/registrationService';
import doceboService from '../services/doceboService';
import { getApiErrorInfo } from '../services/api';
import type { AddonAccessCheck } from '../services/billingService';

import TenantUpgradeCtaComponent from '../components/TenantUpgradeCta';
import { useUserContext } from '../hooks/useUserContext';
import type { AdminSelfAssignResult } from '../hooks/useAdminSelfAssign';

interface UiNotificationState {
  open: boolean;
  severity: AlertColor;
  message: string;
}

// Maps a Docebo learning-object type to the icon + label used in the syllabus list.
// Types come from `flat_los[].items[].type` on Docebo's course detail response.
function getSyllabusItemDisplay(itemType: string): { icon: typeof FileIcon; label: string } {
  switch ((itemType || '').toLowerCase()) {
    case 'file':
      return { icon: FileIcon, label: 'File' };
    case 'tincan':
    case 'xapi':
      return { icon: DescriptionIcon, label: 'xAPI' };
    case 'scormorg':
    case 'scorm':
    case 'authoring':
      return { icon: ScormIcon, label: 'SCORM' };
    case 'test':
      return { icon: QuizIcon, label: 'Test' };
    case 'poll':
      return { icon: SurveyIcon, label: 'Survey' };
    case 'video':
    case 'videofile':
      return { icon: VideoIcon, label: 'Video' };
    case 'audio':
    case 'audiofile':
      return { icon: AudioIcon, label: 'Audio' };
    case 'html':
    case 'htmlpage':
      return { icon: ArticleIcon, label: 'Article' };
    case 'deliverable':
    case 'assignment':
      return { icon: ListIcon, label: 'Assignment' };
    case 'slides':
    case 'presentation':
      return { icon: SlideshowIcon, label: 'Slides' };
    default:
      return { icon: DefaultLessonIcon, label: itemType || 'Lesson' };
  }
}

function formatDateTime(dateStr?: string) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '');
}

function formatCreditLabel(course: Course): string {
  const hours = course.creditHours;
  const categories = [...new Set((course.courseCredits ?? []).map(c => c.educationCategoryName).filter(Boolean))];
  const label = `${hours} CPE Credit${hours !== 1 ? 's' : ''}`;
  return categories.length > 0 ? `${label} · ${categories.join(', ')}` : label;
}

interface OnDemandCourseDetailProps {
  course: Course;
  regStatus: RegistrationStatus | null;
  registration: Registration | null;
  addonCheck: AddonAccessCheck | null;
  recommendedCourses: Course[];
  requestUpgradeLoading: boolean;
  requestUpgradeSuccess: boolean;
  onRegistrationUpdate: (registration: Registration) => void;
  onRequestUpgrade: () => void;
  isTenantAdminProp?: boolean;
  adminSelfAssign?: AdminSelfAssignResult;
  onAdminActivate?: () => void;
}

export default function OnDemandCourseDetail({
  course,
  regStatus,
  registration,
  addonCheck,
  recommendedCourses,
  requestUpgradeLoading,
  requestUpgradeSuccess,
  onRegistrationUpdate,
  onRequestUpgrade,
  isTenantAdminProp,
  adminSelfAssign,
  onAdminActivate,
}: OnDemandCourseDetailProps) {
  const navigate = useNavigate();
  const { isTenantAdmin } = useUserContext();

  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [notification, setNotification] = useState<UiNotificationState>({
    open: false,
    severity: 'info',
    message: '',
  });

  const courseId = course.id;
  const isTenantManaged = addonCheck?.isTenantGated ?? false;
  const hasAccess = addonCheck?.hasAccess ?? false;
  const registrationStatus = registration?.status ?? regStatus?.status;
  const isPending = registrationStatus === 'pending';
  const isFailed = registrationStatus === 'failed';
  const isLaunchable = registrationStatus === 'enrolled' || registrationStatus === 'in_progress' || registrationStatus === 'completed';
  const primaryDescription =
    course.description ||
    course.descriptions?.find((section) => !!section.description)?.description ||
    undefined;
  const courseDescriptionForAi = primaryDescription
    ? stripHtml(primaryDescription).replace(/\s+/g, ' ').trim().slice(0, 700)
    : undefined;
  const fieldsOfStudy = getFieldsOfStudy(course);
  const aiCourseContext = {
    courseId: course.id,
    title: course.title,
    category: fieldsOfStudy.join(', '),
    courseType: 'on-demand',
    creditHours: course.creditHours,
    level: course.level,
    description: courseDescriptionForAi,
    startDate: course.startDate,
    endDate: course.endDate,
  };

  const handleDoceboEnroll = async () => {
    setIsRegistering(true);
    try {
      const result = await doceboService.enroll(courseId);
      if (result.registration) {
        onRegistrationUpdate(result.registration);
      }
      window.open(result.courseUrl, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      const apiError = getApiErrorInfo(err);
      console.error('Docebo enrollment failed:', err);
      setNotification({
        open: true,
        severity: 'error',
        message: apiError.message,
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const handleViewCourse = () => {
    if (!course.url) {
      setNotification({
        open: true,
        severity: 'info',
        message: 'Course launch link is not available yet. Please try enrolling again.',
      });
      return;
    }

    window.open(course.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/courses')}
          sx={{
            textTransform: 'uppercase',
            color: 'primary.main',
            width: 'fit-content',
            fontSize: '14px',
            fontWeight: 500,
            letterSpacing: '0.4px',
            px: 1,
            py: 0.75,
          }}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BookmarkButton courseId={courseId} size="medium" />
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 400, fontSize: { xs: '22px', sm: '26px', md: '32px' }, lineHeight: 1.235, letterSpacing: '0.25px', flex: 1 }}
          >
            {course.title}
          </Typography>
        </Box>
      </Box>

      {/* Meta row */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          component="span"
          sx={(theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            px: 0.875,
            py: '3px',
            borderRadius: '4px',
            bgcolor: theme.palette.status.onDemand.bg,
            color: theme.palette.status.onDemand.fg,
          })}
        >
          On-Demand
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AccessTimeIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>
            {course.creditHours} CPE Hour{course.creditHours !== 1 ? 's' : ''}
            {course.courseCredits?.length > 0 && ` · ${[...new Set(course.courseCredits.map(c => c.educationCategoryName).filter(Boolean))].join(', ')}`}
          </Typography>
        </Box>
        {course.level && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <LevelIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>
              {course.level}
            </Typography>
          </Box>
        )}
        {course.startDate && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CalendarIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>
              {formatDateTime(course.startDate)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Credit-limit hint: this course's delivery method is at/near a cap on one of the user's licenses */}
      <CourseCreditLimitHint course={course} sx={{ mb: 3 }} />

      {/* Three column layout */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          lg: '1fr 360px',
          xl: '1fr 360px 320px',
        },
        gridTemplateAreas: {
          xs: '"main" "registration" "similar" "ai"',
          lg: '"main sidebar" "similar sidebar"',
          xl: '"main registration ai" "similar registration ai"',
        },
        gridAutoRows: 'auto',
        gap: { xs: 2, lg: 3 },
        alignItems: 'start',
      }}>
        {/* Main Content */}
        <Box sx={{ gridArea: 'main', minWidth: 0 }}>
          {/* Main Details Card */}
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              {/* Instructor */}
              {(course.speakers.length > 0 || course.developer) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    src={course.speakers[0]?.avatarUrl ?? undefined}
                    sx={{
                      width: 84,
                      height: 84,
                      bgcolor: 'rgba(0, 0, 0, 0.07)',
                      borderRadius: '6px',
                      flexShrink: 0,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                  </Avatar>
                  <Box>
                    <Typography
                      component="div"
                      sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        lineHeight: 1,
                        mb: 0.75,
                      }}
                    >
                      Instructor
                    </Typography>
                    <Typography
                      sx={{ fontSize: '16px', fontWeight: 600, color: 'text.primary', lineHeight: 1.4, letterSpacing: '-0.01em' }}
                    >
                      {course.speakers.length > 0
                        ? course.speakers.map((s) => (s.displayName || s.name || '').trim()).filter(Boolean).join(', ')
                        : course.developer}
                    </Typography>
                    {course.speakers[0]?.title && (
                      <Typography
                        sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.25, lineHeight: 1.4 }}
                      >
                        {course.speakers[0].title}
                      </Typography>
                    )}
                    {course.speakers.length > 0 && course.developer && (
                      <Typography
                        sx={{ fontSize: '11px', color: 'text.disabled', mt: 0.5, lineHeight: 1.4 }}
                      >
                        Developed by {course.developer}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Description */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 1, px: 2 }}
                >
                  Description
                </Typography>
                {(() => {
                  const fullText = course.description?.replace(/<[^>]*>/g, '') ?? '';
                  const TRUNCATE_LIMIT = 800;
                  const isTruncatable = fullText.length > TRUNCATE_LIMIT;
                  const displayText = isTruncatable && !descriptionExpanded
                    ? fullText.slice(0, TRUNCATE_LIMIT) + '…'
                    : fullText;
                  return (
                    <>
                      <Typography
                        sx={{ fontSize: '15px', lineHeight: 1.6, color: 'text.primary', px: 2, maxWidth: '72ch' }}
                      >
                        {displayText}
                      </Typography>
                      {isTruncatable && (
                        <Button
                          size="small"
                          onClick={() => setDescriptionExpanded(e => !e)}
                          sx={{ ml: 1.5, mt: 0.5, textTransform: 'none', fontSize: '14px' }}
                        >
                          {descriptionExpanded ? 'See Less' : 'See All'}
                        </Button>
                      )}
                    </>
                  );
                })()}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Field of Study */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5, px: 2 }}
                >
                  Field of Study
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '14px', px: 2 }}>
                  {fieldsOfStudy.length > 0 ? fieldsOfStudy.join(', ') : '—'}
                </Typography>
              </Box>

              {course.descriptions
                .filter((section) => !NASBA_HANDLED_DESCRIPTION_TYPES.has(section.descriptionType))
                .map((section) => (
                  <Box key={section.descriptionType}>
                    <Divider sx={{ my: 2 }} />
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 1, px: 2 }}>
                      {getDescriptionTypeLabel(section.descriptionType)}
                    </Typography>
                    <Box
                      sx={{ fontSize: '16px', lineHeight: 1.75, color: 'text.primary', px: 2 }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.description) }}
                    />
                  </Box>
                ))}

              <NasbaDisclosures course={course} />
            </CardContent>
          </Paper>
        </Box>

        {/* Similar Courses */}
        <Box sx={{ gridArea: 'similar', minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <RecommendedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px' }}>
                  Similar Courses
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                {recommendedCourses.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No recommendations available.</Typography>
                ) : (
                  recommendedCourses.map((rec) => (
                    <Card
                      key={rec.id}
                      elevation={0}
                      sx={{
                        minWidth: 228,
                        maxWidth: 228,
                        flexShrink: 0,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '4px',
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Chip
                          label={formatCreditLabel(rec)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 1, borderRadius: '100px', height: '24px', fontSize: '12px' }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 500, fontSize: '16px', lineHeight: 1.5, letterSpacing: '0.15px', mb: 0.5 }}
                        >
                          {rec.title}
                        </Typography>
                        {rec.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 1.5, lineHeight: 1.66, letterSpacing: '0.4px' }}
                          >
                            {stripHtml(rec.description).slice(0, 90)}
                            {stripHtml(rec.description).length > 90 ? '...' : ''}
                          </Typography>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          component={Link}
                          to={`/app/courses/${rec.id}`}
                          endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                          sx={{
                            textTransform: 'uppercase',
                            fontSize: '14px',
                            letterSpacing: '0.46px',
                            fontWeight: 500,
                          }}
                        >
                          View Course
                        </Button>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>
            </CardContent>
          </Paper>
        </Box>

        {/* Sidebar wrapper: single flex column at lg; transparent (display:contents) at xs/xl */}
        <Box sx={{
          gridArea: { lg: 'sidebar' },
          display: { xs: 'contents', lg: 'flex', xl: 'contents' },
          flexDirection: 'column',
          gap: 2,
          minWidth: 0,
        }}>

        {/* Sidebar: registration card first on small screens, syllabus first at lg+ */}
        <Box sx={{ gridArea: 'registration', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Syllabus Card */}
          {course.syllabusItems && course.syllabusItems.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                order: { xs: 2, lg: 1 },
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px', mb: 1.5, px: 2 }}>
                  Course Syllabus{' '}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                    {course.syllabusItems.length} Lesson{course.syllabusItems.length !== 1 ? 's' : ''}
                  </Typography>
                </Typography>
                <Divider sx={{ mb: 0 }} />
                {course.syllabusItems.map((lesson, idx) => {
                  const { icon: LessonIcon, label } = getSyllabusItemDisplay(lesson.itemType);
                  const isLast = idx === course.syllabusItems!.length - 1;
                  return (
                    <Box key={lesson.id}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          px: 2,
                          py: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <LessonIcon sx={{ fontSize: 24, color: 'text.secondary' }} titleAccess={label} />
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: '14px', flex: 1 }}>
                          {lesson.title}
                        </Typography>
                      </Box>
                      {!isLast && <Divider />}
                    </Box>
                  );
                })}
              </CardContent>
            </Paper>
          )}

          {/* Registration Card */}
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              order: { xs: 1, lg: 2 },
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px', mb: 2, px: 2 }}>
                Registration
              </Typography>
              <Box sx={{ px: 2 }}>
                {!hasAccess && isTenantManaged ? (
                  <TenantUpgradeCtaComponent
                    hasPendingRequest={addonCheck?.hasPendingRequest ?? false}
                    loading={requestUpgradeLoading}
                    success={requestUpgradeSuccess}
                    onRequest={onRequestUpgrade}
                    isAdmin={isTenantAdminProp && isTenantManaged}
                    seatAvailable={adminSelfAssign?.seatAvailable ?? null}
                    activating={adminSelfAssign?.activating}
                    activated={adminSelfAssign?.activated}
                    activateError={adminSelfAssign?.error}
                    onActivate={onAdminActivate}
                    onManageBilling={() => navigate('/app/tenant-admin/billing')}
                    moduleLabel="On-Demand"
                  />
                ) : !hasAccess ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.disabled' }}>
                      <LockIcon sx={{ fontSize: 20 }} />
                      <Typography variant="body2" color="text.disabled">
                        On-Demand access required
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Add the On-Demand module to your subscription to access this course.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      startIcon={<UpgradeIcon />}
                      onClick={() => navigate(isTenantAdmin ? '/app/tenant-admin/billing' : '/app/billing')}
                      sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.46px' }}
                    >
                      Upgrade Subscription
                    </Button>
                  </Box>
                ) : isPending ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    startIcon={<CircularProgress size={18} color="inherit" />}
                    disabled
                    sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.46px' }}
                  >
                    Enrollment Pending
                  </Button>
                ) : isLaunchable ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    startIcon={<PlayIcon />}
                    onClick={handleViewCourse}
                    sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.46px' }}
                  >
                    View Course
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    startIcon={isRegistering ? <CircularProgress size={18} color="inherit" /> : <PlayIcon />}
                    onClick={handleDoceboEnroll}
                    disabled={isRegistering}
                    sx={{ textTransform: 'uppercase', fontWeight: 500, letterSpacing: '0.46px' }}
                  >
                    {isRegistering ? 'Enrolling…' : isFailed ? 'Retry Enrollment' : 'Enroll & View'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Paper>
        </Box>

        {/* AI Assistant */}
        <Box
          sx={{
            gridArea: 'ai',
            minWidth: 0,
            alignSelf: 'start',
            position: { xl: 'sticky' },
            top: { xl: 0 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <AIAssistant
              variant="embedded"
              title="Need help with this course?"
              subtitle="Ask for a summary, related learning ideas, or what to focus on next."
              courseContext={aiCourseContext}
              suggestions={[
                { id: 'course-summary', text: `Summarize what I'll learn in ${course.title}` },
                { id: 'course-fit', text: `How does this course support my ${fieldsOfStudy[0] || 'CPE'} goals?` },
                { id: 'similar-courses', text: `Find similar courses as ${course.title} for me` },
              ]}
            />
          </Paper>
        </Box>

        </Box>{/* end sidebar wrapper */}
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

